import { action } from 'mobx';
import { observer } from 'mobx-react';
import { fromArrayMaybe } from 'nonempty-list';
import * as React from 'react';
import { isRequired, StringField } from '../../Types';
import Label from '../Label';
import MultiLine from './MultiLine';
import SingleLine from './SingleLine';
import { T, TPlainTextKey } from '../../../../Translations';

interface Props {
  field: StringField;
  footer?: TPlainTextKey;
  optional?: boolean;
}

const multiLineClassName = (field: StringField): string =>
  field.content.multiline ? 'string' : 'text';

const requiredClassName = (field: StringField): string =>
  isRequired(field) ? 'required' : 'optional';

const errorClassName = (field: StringField): string =>
  field.errors.length > 0 ? 'error has-error' : '';

const onChange = (field: StringField) => action((value: string) => (field.value = value));

const classNames = (field: StringField): string =>
  [multiLineClassName(field), requiredClassName(field), errorClassName(field)].join(' ');

const StringInput: React.FC<Props> = ({ field, footer, optional }) => {
  const InputComponent = field.content.multiline ? MultiLine : SingleLine;
  return (
    <div className={`mb-6 ${classNames(field)}`}>
      <Label field={field} optional={optional} />
      <span>
        <InputComponent field={field} onChange={onChange(field)} />
        {footer ? (
          <p className="text-sm text-gray-500">
            <T kind={footer} />
          </p>
        ) : (
          <></>
        )}
        {fromArrayMaybe(field.errors)
          .map((e) => <span className="help-block">{e.join(', ')}</span>)
          .getOrElse(() => (
            <></>
          ))}
      </span>
    </div>
  );
};

export default observer(StringInput);
